import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Token } from '../../types';
import { useAppContext } from '../../context/AppContext';

interface Props {
  token: Token;
  onClose: () => void;
  articleTokens?: Token[];
  tokenIndex?: number;
}

interface BatchProps {
  tokens: Token[];
  onClose: () => void;
  articleTokens?: Token[];
}

export default function SaveWordModal({ token, onClose, articleTokens, tokenIndex }: Props) {
  const { decks, addDeck, saveWord, lastUsedDeckId } = useAppContext();
  const defaultDeckId = lastUsedDeckId && decks.some(d => d.id === lastUsedDeckId)
    ? lastUsedDeckId
    : decks[0]?.id ?? '';
  const [selectedDeckId, setSelectedDeckId] = useState<string>(defaultDeckId);
  const [newDeckName, setNewDeckName] = useState('');
  const [isCreating, setIsCreating] = useState(decks.length === 0);

  const handleSave = async () => {
    let deckId = selectedDeckId;
    if (isCreating && newDeckName.trim()) {
      const deck = await addDeck(newDeckName.trim());
      deckId = deck.id;
    }
    if (!deckId) return;
    await saveWord(token, deckId, articleTokens, tokenIndex);
    onClose();
  };

  return <DeckPickerModal
    selectedDeckId={selectedDeckId}
    setSelectedDeckId={setSelectedDeckId}
    newDeckName={newDeckName}
    setNewDeckName={setNewDeckName}
    isCreating={isCreating}
    setIsCreating={setIsCreating}
    onSave={handleSave}
    onClose={onClose}
    preview={
      <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-3 mb-4 mt-3">
        <div className="font-semibold text-slate-800 dark:text-slate-100">
          {token.basicForm || token.surface}
          {token.basicForm && token.basicForm !== token.surface && (
            <span className="text-sm font-normal text-slate-400 ml-1">(from {token.surface})</span>
          )}
        </div>
        <div className="text-sm text-primary-600 dark:text-primary-400">{token.reading}</div>
        <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{token.translation}</div>
      </div>
    }
  />;
}

export function BatchSaveModal({ tokens, onClose, articleTokens }: BatchProps) {
  const { decks, addDeck, saveBatchWords, lastUsedDeckId } = useAppContext();
  const defaultDeckId = lastUsedDeckId && decks.some(d => d.id === lastUsedDeckId)
    ? lastUsedDeckId
    : decks[0]?.id ?? '';
  const [selectedDeckId, setSelectedDeckId] = useState<string>(defaultDeckId);
  const [newDeckName, setNewDeckName] = useState('');
  const [isCreating, setIsCreating] = useState(decks.length === 0);
  const [saving, setSaving] = useState(false);

  const validCount = tokens.filter(t => t.translation).length;

  const handleSave = async () => {
    let deckId = selectedDeckId;
    if (isCreating && newDeckName.trim()) {
      const deck = await addDeck(newDeckName.trim());
      deckId = deck.id;
    }
    if (!deckId) return;
    setSaving(true);
    await saveBatchWords(tokens, deckId, articleTokens);
    setSaving(false);
    onClose();
  };

  return <DeckPickerModal
    selectedDeckId={selectedDeckId}
    setSelectedDeckId={setSelectedDeckId}
    newDeckName={newDeckName}
    setNewDeckName={setNewDeckName}
    isCreating={isCreating}
    setIsCreating={setIsCreating}
    onSave={handleSave}
    onClose={onClose}
    saveLabel={saving ? 'Saving...' : `Add ${validCount} words`}
    saveDisabled={saving}
    preview={
      <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-3 mb-4 mt-3">
        <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
          Add {validCount} vocabulary words to a deck
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Words already in the deck will be skipped
        </div>
      </div>
    }
  />;
}

interface DeckPickerProps {
  selectedDeckId: string;
  setSelectedDeckId: (id: string) => void;
  newDeckName: string;
  setNewDeckName: (name: string) => void;
  isCreating: boolean;
  setIsCreating: (v: boolean) => void;
  onSave: () => void;
  onClose: () => void;
  preview: React.ReactNode;
  saveLabel?: string;
  saveDisabled?: boolean;
}

function DeckPickerModal({
  selectedDeckId, setSelectedDeckId,
  newDeckName, setNewDeckName,
  isCreating, setIsCreating,
  onSave, onClose, preview,
  saveLabel = 'Save',
  saveDisabled = false,
}: DeckPickerProps) {
  const { decks } = useAppContext();

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/40 z-[10000] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-md p-6"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={e => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-1">Save Word to Deck</h3>
          {preview}

          {!isCreating && decks.length > 0 ? (
            <>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">Choose a deck</label>
              <div className="space-y-1.5 mb-4 max-h-40 overflow-y-auto">
                {decks.map(deck => (
                  <label
                    key={deck.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer border transition-colors ${
                      selectedDeckId === deck.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                        : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                    }`}
                  >
                    <input
                      type="radio"
                      name="deck"
                      value={deck.id}
                      checked={selectedDeckId === deck.id}
                      onChange={() => setSelectedDeckId(deck.id)}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-200">{deck.name}</span>
                  </label>
                ))}
              </div>
              <button
                onClick={() => setIsCreating(true)}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-4 block"
              >
                + Create new deck
              </button>
            </>
          ) : (
            <>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
                {decks.length === 0 ? 'Create your first deck' : 'New deck name'}
              </label>
              <input
                type="text"
                placeholder="e.g. IT/Technology, Business Terms..."
                value={newDeckName}
                onChange={e => setNewDeckName(e.target.value)}
                autoFocus
                className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
              />
              {decks.length > 0 && (
                <button
                  onClick={() => setIsCreating(false)}
                  className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 mb-4 block"
                >
                  Choose existing deck instead
                </button>
              )}
            </>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saveDisabled || (isCreating ? !newDeckName.trim() : !selectedDeckId)}
              className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:bg-slate-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
            >
              {saveLabel}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
